import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons/faExternalLinkAlt';

import React, { useCallback, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { noop } from 'ui-components/default-props/noop';
import { Text, TextType } from 'ui-components/components/text';
import { Button } from 'ui-components/components/button';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { useTrackWorkflow, WorkflowSteps } from '../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../tracking/tracking-constants';
import '../../../prop-types/add-on.types';
import { isAddOnAvailableForTrial } from '../../../pages/settings/add-on-marketplace/helpers';
import { getUpsellModalContent } from '../helper';
import { CodeToFormattedMessageProps } from '../../../errors/user-friendly-error-message-service/code-to-formatted-message-props';
import { DEFAULT_TRIAL_LENGTH_IN_DAYS } from '../../../constants/add-ons';

import { messages } from './messages';
import { useAddOnsActionContext } from '../../../pages/settings/add-on-marketplace/add-ons-action-context';
import { ReturnRabbit } from '../../../constants/upsell-modal-content-codes';
import style from './confirm-upsell-button.module.css';

/**
 * @param {string} addOnName
 * @param {Error} error
 * @param {() => void} onClickEnable
 */
const showEnableFailureNotification = (addOnName, error, onClickEnable) => {
  let messageContent = <FormattedMessage values={{ addOnName }} {...messages.activateAddOnFailureNotificationText} />;

  if (error?.Code) {
    const messageProps = CodeToFormattedMessageProps(error.Code);
    messageContent = <FormattedMessage {...messageProps} />;
  }

  NotificationService.showNotification({
    notificationType: NOTIFICATION_TYPE_ERROR,
    title: <FormattedMessage values={{ addOnName }} {...messages.activateAddOnFailureNotificationTitle} />,
    content: messageContent,
    buttons: [
      {
        text: <FormattedMessage {...messages.retryButton} />,
        callback: onClickEnable,
      },
    ],
  });
};

const UpsellButtonContent = ({ canTrial, trialLengthInDays, addOnCode, addOnName }) => {
  const intl = useIntl();
  if (canTrial) {
    return (
      <FormattedMessage
        {...messages.tryFreeForXDays}
        values={{
          trialLengthInDays,
          formattedDays: intl.formatNumber(trialLengthInDays),
        }}
      />
    );
  }

  const adOnTextMessage = addOnCode === ReturnRabbit ? messages.activateAndConfigureReturnRabbit : messages.activateAddOn;
  return (
    <>
      {addOnCode !== ReturnRabbit && <FontAwesomeIcon className={style['upsell-icon']} icon={faBolt} />}
      <Text formattedMessageValues={{ addOnName }} textType={TextType.Label} value={adOnTextMessage} />
      {addOnCode === ReturnRabbit && <FontAwesomeIcon className={style['upsell-icon-right']} icon={faExternalLinkAlt} />}
    </>
  );
};

/**
 * @param {object} props
 * @param {AddOnPropType} props.addOn
 * @param {() => void} props.onCloseModal
 * @param {(code: string) => void} props.onError
 * @returns {JSX.Element}
 */
export const ConfirmAddOnUpsellButton = ({ addOn, onCloseModal, onError = noop }) => {
  const [isActivating, setIsActivating] = useState(false);
  const intl = useIntl();
  const content = getUpsellModalContent(addOn.code);
  const trackAddOnWorkflow = useTrackWorkflow(Workflows.ADD_ON_FLOW);

  const canTrial = isAddOnAvailableForTrial(addOn);
  const addOnName = intl.formatMessage(content.name);

  const { onEnableAddOn } = useAddOnsActionContext();

  const onClickEnable = useCallback(async () => {
    try {
      setIsActivating(true);
      await onEnableAddOn(addOn.code);
    } catch (e) {
      const error = e?.response?.data?.Errors[0];
      trackAddOnWorkflow(WorkflowSteps.ERROR, { error: e });
      onError(addOn.code);
      showEnableFailureNotification(addOnName, error, onClickEnable);
    }
    setIsActivating(false);
    onCloseModal();
  }, [onCloseModal, onEnableAddOn, addOn.code, trackAddOnWorkflow, onError, addOnName]);

  const trialLengthInDays = addOn?.defaultTrialPeriodInDays ?? DEFAULT_TRIAL_LENGTH_IN_DAYS;

  return (
    <Button disabled={isActivating} onClick={onClickEnable} theme='upsell'>
      <UpsellButtonContent addOnCode={addOn.code} addOnName={addOnName} canTrial={canTrial} trialLengthInDays={trialLengthInDays} />
    </Button>
  );
};
